import type { RecipeListItem } from '@recipes/contracts';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
} from '@recipes/ui';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RecipePlaceholder } from '@/features/appearance/recipe-placeholder';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useDebounced<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Search box + result list backed by GET /recipes?q=. Single or multi select. */
export function RecipeSearchList({
  selected,
  onToggle,
  multiple = true,
  excludeIds = [],
  autoFocus,
}: {
  selected: string[];
  onToggle: (recipe: RecipeListItem) => void;
  multiple?: boolean;
  excludeIds?: string[];
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const query = useDebounced(q.trim());
  const id = useId();
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.recipes.list({ q: query, limit: 30, picker: true }),
    queryFn: () => api.recipes.list({ q: query || undefined, limit: 30 }),
  });
  const items = (data?.items ?? []).filter((r) => !excludeIds.includes(r.id));

  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor={id} className="sr-only">
          {t('common.search')}
        </Label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id={id}
            type="search"
            className="pl-9"
            placeholder={t('shared.recipePicker.searchPlaceholder')}
            value={q}
            autoFocus={autoFocus}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>
      <div
        role={multiple ? 'group' : 'listbox'}
        aria-label={t('shared.recipePicker.results')}
        className="max-h-72 space-y-1 overflow-y-auto rounded-md border p-1"
      >
        {isLoading && (
          <div className="p-4 text-center">
            <Spinner label={t('app.loading')} />
          </div>
        )}
        {!isLoading && items.length === 0 && (
          <p className="p-4 text-center text-sm text-muted-foreground">{t('common.noResults')}</p>
        )}
        {items.map((recipe) => {
          const checked = selected.includes(recipe.id);
          return (
            <label
              key={recipe.id}
              className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-md px-2 py-1 hover:bg-accent has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-ring"
            >
              {multiple ? (
                <Checkbox checked={checked} onCheckedChange={() => onToggle(recipe)} />
              ) : (
                <input
                  type="radio"
                  name={`${id}-recipe`}
                  className="size-5 accent-primary"
                  checked={checked}
                  onChange={() => onToggle(recipe)}
                />
              )}
              {recipe.coverThumbnailUrl ? (
                <img
                  src={recipe.coverThumbnailUrl}
                  alt=""
                  className="size-10 shrink-0 rounded object-cover"
                />
              ) : (
                <RecipePlaceholder recipe={recipe} className="size-10 shrink-0 rounded" small />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{recipe.title}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {[
                    recipe.totalTimeMinutes
                      ? `${recipe.totalTimeMinutes} ${t('common.minutes')}`
                      : null,
                    `${recipe.servings} ${t('common.servings')}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export function RecipePickerDialog({
  open,
  onOpenChange,
  onConfirm,
  multiple = true,
  excludeIds,
  title,
  description,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (recipes: RecipeListItem[]) => void | Promise<void>;
  multiple?: boolean;
  excludeIds?: string[];
  title?: string;
  description?: string;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<RecipeListItem[]>([]);
  useEffect(() => {
    if (!open) setSelected([]);
  }, [open]);

  const toggle = (recipe: RecipeListItem) => {
    setSelected((prev) => {
      const exists = prev.some((r) => r.id === recipe.id);
      if (!multiple) return exists ? [] : [recipe];
      return exists ? prev.filter((r) => r.id !== recipe.id) : [...prev, recipe];
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} fullscreenOnMobile>
        <DialogHeader>
          <DialogTitle>{title ?? t('shared.recipePicker.title')}</DialogTitle>
          <DialogDescription>
            {description ?? t('shared.recipePicker.description')}
          </DialogDescription>
        </DialogHeader>
        <RecipeSearchList
          selected={selected.map((r) => r.id)}
          onToggle={toggle}
          multiple={multiple}
          excludeIds={excludeIds}
          autoFocus
        />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={selected.length === 0}
            loading={loading}
            onClick={() => void onConfirm(selected)}
          >
            {multiple
              ? t('shared.recipePicker.addCount', { count: selected.length })
              : t('shared.recipePicker.select')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
